import { useCallback, useEffect } from 'react';
import { easeOutQuad, useDrag } from '@direct-frontend/components';
import { useDrawerContext } from '../../DrawerContext';
import { DRAWER_CONFIG } from '../constants';
import { getNearestScrollableParent } from '../getNearestScrollableParent';
import { type DrawerProps } from '../types';
import { updateDrawerStyles } from '../updateDrawerStyles';

interface DragStateData {
    /**
     * Находится ли элемент, на котором началось событие drag-а, в контенте шторки
     */
    isTargetUnderContent?: boolean;

    /**
     * Произошел ли скролл контента по оси X
     */
    isScrolledX?: boolean;

    /**
     * Произошел ли скролл контента по оси Y
     */

    isScrolledY: boolean;
    /**
     * Элемент контента со скролом
     */
    target: Element;

    /** Флаг для отключения перемещения шторки для текущего свайпа */
    isDisableDrag?: boolean;
}

type UseDrawerDragParams = {
    /**
     * Происходит ли анимация открытия/закрытия шторки
     */
    isAnimated?: boolean;

    /**
     * Признак возможности перемещения шторки вверх (при выключении шторку нельзя двигать мышью или пальцем)
     */
    isNegativeDraggable?: boolean;
} & Pick<DrawerProps, 'maxSize' | 'onClose' | 'isDraggable'>;

type DragEventType = 'start' | 'move' | 'end';

const hasElementKeyboard = (): boolean => {
    const activeElement = document.activeElement;

    if (!activeElement) {
        return false;
    }

    const isInputElement = activeElement instanceof HTMLInputElement;
    const isTextareaElement = activeElement instanceof HTMLTextAreaElement;

    const inputWithoutKeyboard = ['radio', 'checkbox'];

    const hasInputKeyboard = isInputElement && !inputWithoutKeyboard.includes(activeElement.type);

    return hasInputKeyboard || isTextareaElement;
};

const checkVirtualKeyboard = (type: DragEventType, isDragDisabled: boolean): boolean => {
    const isTypeStart = type === 'start';

    if (hasElementKeyboard() && isTypeStart) {
        return true;
    }

    return isDragDisabled;
};

const blurVirtualKeyboard = (type: DragEventType, isDragDisabled: boolean, movement: number): void => {
    const activeElement = document.activeElement;

    if (!activeElement || !(activeElement instanceof HTMLElement)) {
        return;
    }

    const isTypeMove = type === 'move';
    const shouldCloseKeyboard = Math.abs(movement) > DRAWER_CONFIG.CLOSE_VIRTUAL_KEYBOARD_PX;

    if (hasElementKeyboard() && isTypeMove && isDragDisabled && shouldCloseKeyboard) {
        activeElement.blur();
    }
};

interface HandleDragStartParams {
    event: { target: EventTarget | null };
    data?: DragStateData;
    contentRef: React.RefObject<HTMLElement | null>;
    setScrollableNode: (node: HTMLElement) => void;
    isDisableDrag: boolean;
}

const handleDragStart = ({
    event,
    data,
    contentRef,
    setScrollableNode,
    isDisableDrag,
}: HandleDragStartParams): DragStateData => {
    const scrollableTarget = getNearestScrollableParent(event.target as HTMLElement);
    const initialScrollPosition = scrollableTarget.scrollTop;

    const isScrolledY =
        initialScrollPosition !== 0 &&
        initialScrollPosition !== scrollableTarget.scrollHeight - scrollableTarget.clientHeight;

    setScrollableNode(scrollableTarget);

    return {
        ...data,
        isTargetUnderContent: contentRef.current?.contains(scrollableTarget),
        isScrolledY,
        target: scrollableTarget,
        isDisableDrag,
    };
};

interface HandleDragMoveOrEndParams {
    event: { target: EventTarget | null };
    data?: DragStateData;
    hadMove: boolean;
    movement: { x: number; y: number };
    velocity: { x: number; y: number };
    isDisableDrag: boolean;
}

const handleDragMoveOrEnd = ({
    event,
    data,
    hadMove,
    movement,
    velocity,
    isDisableDrag,
}: HandleDragMoveOrEndParams): DragStateData => {
    const scrollableTarget = data?.target || getNearestScrollableParent(event.target as HTMLElement);

    let isScrolledX = false;
    let isScrolledY = false;

    if (hadMove) {
        const { y: my } = movement;
        const { x: vx, y: vy } = velocity;

        isScrolledX = Math.abs(vx) > Math.abs(vy);

        const isScrolledStart = scrollableTarget.scrollTop > 0;
        const isScrolledEnd =
            scrollableTarget.scrollHeight - scrollableTarget.clientHeight > scrollableTarget.scrollTop;

        isScrolledY = my > 0 ? isScrolledStart : isScrolledEnd;
    }

    const isScrolledXShouldChange = data?.isScrolledX === undefined || (data?.isScrolledX === true && !isScrolledX);

    isScrolledX = Boolean(isScrolledXShouldChange ? isScrolledX : data?.isScrolledX);

    return {
        ...data,
        target: scrollableTarget,
        isScrolledY: Boolean(data?.isScrolledY || isScrolledY || isScrolledX),
        isScrolledX,
        isDisableDrag,
    };
};

interface HandleDragEndParams {
    movement: { y: number };
    velocity: { y: number };
    drawerSize: number;
    snapPoints: Array<{ snap: number }>;
    currentSnapPoint: number;
    setSnapPoint: (index: number) => void;
    setDrawerPosition: (position: number) => void;
    snapPointSize: number;
    handleClose: () => void;
}

const handleDragEnd = ({
    movement,
    velocity,
    drawerSize,
    snapPoints,
    currentSnapPoint,
    setSnapPoint,
    setDrawerPosition,
    snapPointSize,
    handleClose,
}: HandleDragEndParams): void => {
    const { y: my } = movement;
    const { y: vy } = velocity;

    if (Math.abs(vy) >= DRAWER_CONFIG.CLOSE_VELOCITY) {
        if (snapPoints.length && !(currentSnapPoint === 0 && vy > 0)) {
            if (vy > 0 && currentSnapPoint > 0) {
                setSnapPoint(currentSnapPoint - 1);

                return;
            } else if (vy < 0 && currentSnapPoint < snapPoints.length - 1) {
                setSnapPoint(currentSnapPoint + 1);

                return;
            }
        } else if (vy > 0) {
            handleClose();

            return;
        }

        setSnapPoint(0);
    } else if (my / drawerSize >= DRAWER_CONFIG.CLOSE_COEFFICIENT) {
        handleClose();

        return;
    }

    if (snapPoints.length) {
        let snapIndex = currentSnapPoint;

        snapPoints.every((point, index) => {
            if (drawerSize - my > point.snap) {
                snapIndex = index;

                return true;
            }

            return false;
        });

        setSnapPoint(snapIndex);

        if (snapIndex === currentSnapPoint) {
            setDrawerPosition(snapPointSize);
        }
    } else {
        setDrawerPosition(snapPointSize);
    }
};

interface HandleDragMoveParams {
    movement: { y: number };
    isNegativeDraggable: boolean;
    snapPointSize: number;
    currentSnapPoint: number;
    snapPointsLength: number;
    setDrawerPosition: (position: number) => void;
}

const handleDragMove = ({
    movement,
    isNegativeDraggable,
    snapPointSize,
    currentSnapPoint,
    snapPointsLength,
    setDrawerPosition,
}: HandleDragMoveParams): void => {
    const { y: my } = movement;
    const movementY = my + snapPointSize;

    if (!isNegativeDraggable && movementY < 0) {
        return;
    }

    const isLastSnapPoint = currentSnapPoint >= snapPointsLength - 1;

    if (isLastSnapPoint) {
        setDrawerPosition(Math.max(my, easeOutQuad(-Math.log(Math.abs(my)))) + snapPointSize);

        return;
    }

    setDrawerPosition(movementY);
};

export const useDrawerDrag = ({
    isDraggable = true,
    isAnimated,
    isNegativeDraggable = true,
    maxSize,
}: UseDrawerDragParams) => {
    const {
        isOpen,
        snapPoint: { snapPointSize, setSnapPoint, snapPoints, currentSnapPoint },
        refs: { containerRef, contentRef, drawerRef },
        scroll: { setScrollableNode },
        onClose,
    } = useDrawerContext();

    const setDrawerPosition = useCallback(
        (position: number) =>
            updateDrawerStyles({
                containerRef,
                drawerRef,
                position,
                maxSize,
                defaultPosition: snapPointSize,
                contentRef,
            }),
        [containerRef, contentRef, drawerRef, maxSize, snapPointSize]
    );

    const handleClose = useCallback(() => {
        setSnapPoint(0);
        onClose();
    }, [onClose, setSnapPoint]);

    useEffect(() => {
        if (isOpen) {
            setDrawerPosition(snapPointSize > 0 ? snapPointSize : 0);
        }
    }, [isOpen, setDrawerPosition, snapPointSize]);

    const { dragProps } = useDrag<DragStateData>((dragState, setData) => {
        if (!drawerRef.current || !contentRef.current || !isOpen || !isAnimated || !isDraggable) {
            return;
        }

        const drawerSize = drawerRef.current.clientHeight - snapPointSize;
        const { type, hadMove, event, data: stateData } = dragState;

        let isDisableDrag = Boolean(stateData?.isDisableDrag);

        isDisableDrag = checkVirtualKeyboard(type, isDisableDrag);

        const movement = hadMove ? dragState.movement : { x: 0, y: 0 };
        const velocity = hadMove ? dragState.velocity : { x: 0, y: 0 };

        blurVirtualKeyboard(type, isDisableDrag, movement.y);

        const data =
            type === 'start'
                ? handleDragStart({ event, data: stateData, contentRef, setScrollableNode, isDisableDrag })
                : handleDragMoveOrEnd({ event, data: stateData, hadMove, movement, velocity, isDisableDrag });

        setData(data);

        if (isDisableDrag || (stateData?.isTargetUnderContent && stateData?.isScrolledY)) {
            return;
        }

        if (type === 'end' && hadMove) {
            handleDragEnd({
                movement,
                velocity,
                drawerSize,
                snapPoints,
                currentSnapPoint,
                setSnapPoint,
                setDrawerPosition,
                snapPointSize,
                handleClose,
            });
        }

        if (type === 'move' && hadMove) {
            handleDragMove({
                movement,
                isNegativeDraggable,
                snapPointSize,
                currentSnapPoint,
                snapPointsLength: snapPoints.length,
                setDrawerPosition,
            });
        }
    });

    return { dragProps };
};
